/**
 * NumberedPagination Web Component
 * Handles both server-rendered and client-side numbered pagination
 * Can be used as <numbered-pagination> custom element or integrated with the Listing component
 */
export default class NumberedPagination extends HTMLElement {
    constructor() {
        super();

        // Default options
        this.options = {
            currentPage: 1,
            totalPages: 1,
            perPage: 10,
            maxVisiblePages: 6,
            showFirstLast: true,
            showPrevNext: true,
            urlParam: "page",
            onPageChange: null,
        };

        this.currentPage = this.options.currentPage;
        this.totalPages = this.options.totalPages;
    }

    connectedCallback() {
        // Add default classes for styling consistency
        if (!this.className) {
            this.className = "pagination flex-list not-prose";
        }

        // Parse attributes when element is connected to the DOM
        this.parseAttributes();
        this.init();
    }

    static get observedAttributes() {
        return [
            "current-page",
            "total-pages",
            "per-page",
            "max-visible-pages",
            "url-param",
            "show-first-last",
            "show-prev-next",
        ];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue !== newValue) {
            this.parseAttributes();
            if (this.isConnected) {
                this.init();
            }
        }
    }

    parseAttributes() {
        // Parse data attributes from HTML
        this.options.currentPage =
            parseInt(this.getAttribute("current-page")) || 1;
        this.options.totalPages =
            parseInt(this.getAttribute("total-pages")) || 1;
        this.options.perPage = parseInt(this.getAttribute("per-page")) || 12;
        this.options.maxVisiblePages =
            parseInt(this.getAttribute("max-visible-pages")) || 4;
        this.options.urlParam = this.getAttribute("url-param") || "page";
        this.options.showFirstLast =
            this.getAttribute("show-first-last") !== "false";
        this.options.showPrevNext =
            this.getAttribute("show-prev-next") !== "false";
        this.options.scrollToTop =
            this.getAttribute("scroll-to-top") !== "false";
        this.options.scrollTo = this.getAttribute("scroll-to");

        this.currentPage = this.options.currentPage;
        this.totalPages = this.options.totalPages;
    }

    init() {
        // Check if pagination is needed
        if (this.totalPages <= 1) {
            this.hidden = true;
            return;
        }

        this.hidden = false;
        this.initializeElements();
        this.render();
        this.attachEventListeners();
    }

    initializeElements() {
        if (this.options.scrollToTop) {
            this.scrollToElement = this.options.scrollTo
                ? document.querySelector(this.options.scrollTo)
                : document.documentElement;
        }
    }

    attachEventListeners() {
        // Remove existing listeners to avoid duplicates
        if (this.clickHandler) {
            this.removeEventListener("click", this.clickHandler);
        }

        this.clickHandler = (e) => {
            const link = e.target.closest("[data-page]");
            if (!link) return;

            e.preventDefault();

            const page = parseInt(link.dataset.page);
            if (
                page &&
                page !== this.currentPage &&
                page >= 1 &&
                page <= this.totalPages
            ) {
                this.goToPage(page);
            }
        };

        this.addEventListener("click", this.clickHandler);
    }

    goToPage(page) {
        const oldPage = this.currentPage;
        this.currentPage = page;

        // Update URL if not in callback mode
        if (!this.options.onPageChange) {
            this.updateUrl();
        }

        // Re-render pagination
        this.render();

        // Call callback if provided
        if (
            this.options.onPageChange &&
            typeof this.options.onPageChange === "function"
        ) {
            this.options.onPageChange(page, oldPage);
        }

        // Scroll to top of listing (optional)
        if (this.options.scrollToTop !== false && this.scrollToElement) {
            setTimeout(() => {
                this.scrollToElement.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }, 250);
        }
    }

    updateUrl() {
        const url = new URL(window.location);

        if (this.currentPage === 1) {
            url.searchParams.delete(this.options.urlParam);
        } else {
            url.searchParams.set(this.options.urlParam, this.currentPage);
        }

        window.history.pushState({}, "", url);
    }

    getVisiblePages() {
        const maxVisible = this.options.maxVisiblePages;
        const current = this.currentPage;
        const total = this.totalPages;

        if (total <= maxVisible) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }

        const half = Math.floor(maxVisible / 2);
        let start = Math.max(1, current - half);
        let end = Math.min(total, start + maxVisible - 1);

        // Adjust start if we're near the end
        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }

        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }

    render() {
        const visiblePages = this.getVisiblePages();
        const hasPrev = this.currentPage > 1;
        const hasNext = this.currentPage < this.totalPages;

        let html = '<ul class="pagination__items flex-list" role="list">';

        // Previous button (integrated with first page if showPrevNext is true)
        if (hasPrev && this.options.showPrevNext) {
            html += `
                <li class="pagination__nav pagination__nav--prev">
                    <a href="#" data-page="${this.currentPage - 1}"
                       class="mask-icon">
                        <span class="sr-only">Previous</span>
                    </a>
                </li>
            `;
        }

        // First page + ellipsis if needed
        if (this.options.showFirstLast && visiblePages[0] > 1) {
            html += `
                <li class="pagination__item pagination__item--link">
                    <a href="#" data-page="1">1</a>
                </li>
            `;

            if (visiblePages[0] > 2) {
                html += `
                    <li class="pagination__item pagination__item--ellipsis">
                        <span>…</span>
                    </li>
                `;
            }
        }

        // Visible page numbers
        visiblePages.forEach((page) => {
            if (page === this.currentPage) {
                html += `
                    <li class="pagination__item pagination__item--current">
                        <span>${page}</span>
                    </li>
                `;

                // Next button
                if (hasNext && this.options.showPrevNext) {
                    html += `
                        <li class="pagination__nav pagination__nav--next">
                            <a href="#" data-page="${this.currentPage + 1}"
                               class="mask-icon">
                                <span class="sr-only">Next</span>
                            </a>
                        </li>
                    `;
                }
            } else {
                html += `
                    <li class="pagination__item pagination__item--link">
                        <a href="#" data-page="${page}">${page}</a>
                    </li>
                `;
            }
        });

        // Last page + ellipsis if needed
        const lastVisible = visiblePages[visiblePages.length - 1];
        if (this.options.showFirstLast && lastVisible < this.totalPages) {
            if (lastVisible < this.totalPages - 1) {
                html += `
                    <li class="pagination__item pagination__item--ellipsis">
                        <span>…</span>
                    </li>
                `;
            }

            html += `
                <li class="pagination__item pagination__item--link">
                    <a href="#" data-page="${this.totalPages}">${this.totalPages}</a>
                </li>
            `;
        }

        html += "</ul>";

        this.innerHTML = html;
    }

    // Public API methods
    setCurrentPage(page) {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            this.render();
        }
    }

    setTotalPages(total) {
        this.totalPages = total;
        this.currentPage = Math.min(this.currentPage, total);
        this.init();
    }

    update(options) {
        this.options = { ...this.options, ...options };
        if (options.currentPage !== undefined) {
            this.currentPage = options.currentPage;
        }
        if (options.totalPages !== undefined) {
            this.totalPages = options.totalPages;
        }
        this.init();
    }
}

// Register the web component
if (!customElements.get("numbered-pagination")) {
    customElements.define("numbered-pagination", NumberedPagination);
}
